import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";

// /book-freeway — Performance & appearance inquiries.
// Hero image: news-freefest-2026-01.jpg (preserved natural proportions).
// Form posts to /api/booking which persists to the DB and logs the intended
// notification recipient (BOOKING_NOTIFICATION_EMAIL). No availability,
// pricing, response-time, or booking confirmation is promised.
export default function BookFreeway() {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "",
    city: "", state: "", event_date: "", event_type: "",
    event_details: "", message: "",
  });
  const [status, setStatus] = useState(null); // null | 'loading' | 'ok' | 'error'
  const [errMsg, setErrMsg] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email.includes("@")) { setStatus("error"); setErrMsg("Please add your name and a valid email."); return; }
    setStatus("loading");
    try {
      const res = await fetch(`${API}/api/booking`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Please try again.");
      setStatus("ok");
    } catch (err) {
      setStatus("error");
      setErrMsg(err.message || "Something went wrong.");
    }
  };

  return (
    <div className="App bg-black text-white min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        {/* Hero — news-freefest-2026-01.jpg, natural proportions preserved */}
        <div className="relative w-full h-[52vh] md:h-[64vh] bg-black overflow-hidden">
          <img
            src="/assets/news/freefest-2026/news-freefest-2026-01.jpg"
            alt=""
            data-testid="book-hero-image"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: "center 30%" }}
          />
          <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black via-black/60 to-transparent" />
          <div className="relative z-10 h-full flex items-end px-4 md:px-10 pb-10 md:pb-14">
            <div className="max-w-3xl">
              <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#F4C10A] mb-3">
                Bookings
              </div>
              <h1
                data-testid="book-heading"
                className="font-wide uppercase text-white text-5xl sm:text-6xl md:text-7xl leading-[0.95]"
              >
                Book Freeway
              </h1>
              <div className="mt-3 font-mono text-[11px] md:text-xs tracking-[0.35em] uppercase text-white/80">
                Performance &amp; Appearance Inquiries
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="max-w-3xl mx-auto px-4 md:px-10 pt-14 md:pt-20 pb-20">
          {status === "ok" ? (
            <div data-testid="book-success" className="border border-[#F4C10A]/40 bg-white/5 p-8 text-center">
              <div className="font-wide uppercase text-2xl text-[#F4C10A]">Inquiry Received</div>
              <p className="mt-3 font-body text-white/80 text-base leading-relaxed">
                Thanks for reaching out. Your booking inquiry has been recorded and forwarded to
                the Freedom Thinkers Academy team. Availability, timing, and next steps will be
                confirmed by the team directly.
              </p>
              <Link
                to="/"
                data-testid="book-back-home"
                className="mt-6 inline-block font-mono text-[10px] tracking-[0.3em] uppercase text-white/80 hover:text-[#F4C10A]"
              >
                ← Back Home
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="book-form" className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Name" testid="book-field-name" required>
                <input required value={form.name} onChange={set("name")} data-testid="book-input-name" className={INPUT} />
              </Field>
              <Field label="Company / Organization / Venue" testid="book-field-company">
                <input value={form.company} onChange={set("company")} data-testid="book-input-company" className={INPUT} />
              </Field>
              <Field label="Email" testid="book-field-email" required>
                <input type="email" required value={form.email} onChange={set("email")} data-testid="book-input-email" className={INPUT} />
              </Field>
              <Field label="Phone" testid="book-field-phone">
                <input value={form.phone} onChange={set("phone")} data-testid="book-input-phone" className={INPUT} />
              </Field>
              <Field label="City" testid="book-field-city">
                <input value={form.city} onChange={set("city")} data-testid="book-input-city" className={INPUT} />
              </Field>
              <Field label="State" testid="book-field-state">
                <input value={form.state} onChange={set("state")} data-testid="book-input-state" className={INPUT} />
              </Field>
              <Field label="Event Date" testid="book-field-event-date">
                <input type="date" value={form.event_date} onChange={set("event_date")} data-testid="book-input-event-date" className={INPUT} />
              </Field>
              <Field label="Event Type" testid="book-field-event-type">
                <input value={form.event_type} onChange={set("event_type")} data-testid="book-input-event-type" className={INPUT} placeholder="Festival · Private · Corporate · TV · Podcast" />
              </Field>
              <Field label="Event / Venue Details" testid="book-field-event-details" className="md:col-span-2">
                <textarea rows={3} value={form.event_details} onChange={set("event_details")} data-testid="book-input-event-details" className={INPUT} />
              </Field>
              <Field label="Message / Additional Information" testid="book-field-message" className="md:col-span-2">
                <textarea rows={4} value={form.message} onChange={set("message")} data-testid="book-input-message" className={INPUT} />
              </Field>

              {status === "error" && (
                <div data-testid="book-error" className="md:col-span-2 text-red-400 text-sm">{errMsg}</div>
              )}

              <div className="md:col-span-2 mt-2">
                <button
                  type="submit"
                  data-testid="book-submit"
                  disabled={status === "loading"}
                  className="w-full md:w-auto bg-[#F4C10A] text-black font-display uppercase text-sm tracking-[0.24em] px-10 py-4 hover:bg-white transition-colors disabled:opacity-50"
                >
                  {status === "loading" ? "Submitting…" : "Submit Booking Inquiry"}
                </button>
              </div>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

const INPUT = "w-full bg-transparent border border-white/25 focus:border-[#F4C10A] outline-none px-3 py-2.5 text-white placeholder-white/30 font-mono text-sm";

function Field({ label, testid, required, children, className = "" }) {
  return (
    <label data-testid={testid} className={`block ${className}`}>
      <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/60 mb-2">
        {label}{required ? " *" : ""}
      </div>
      {children}
    </label>
  );
}
